/**
 * The spell forge.
 *
 * Three independent inputs, exactly as the design calls for: a short English
 * chant, a free-form wish, and a gesture registered twice. Nothing here judges
 * the wish — it is sent for interpretation, one sentence comes back, and the
 * player confirms. The numbers shown alongside come from the fixed balance
 * rules, never from the model.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LEXICON_EXAMPLES, type GesturePoint } from '@spell/shared-types';
import { buildTemplate } from '@spell/gesture-engine';
import { balanceFromInterpretation, estimateChant } from '@spell/combat-rules';
import { interpretLocally } from '@spell/ai-interpreter/local';
import { normalizeChant, type SpellStats } from '@spell/spell-schema';
import { GesturePad } from './GesturePad';
import { LexiconPicker } from './LexiconPicker';
import type { GestureRecorder } from '../gesture/capture';
import { serverHttpUrl } from '../networking/net';
import { chantRecognizer, mic, speechSupported } from '../voice/mic';
import { sfx } from '../audio/sfx';

/** Mirrors `spellDraftSchema.chant`; the word bank must not overrun it. */
const CHANT_MAX = 48;

export interface CraftResult {
  chant: string;
  description: string;
  gestureSamples: GesturePoint[][];
}

export interface SpellCraftProps {
  type: 'parry' | 'signature';
  title: string;
  intro?: React.ReactNode;
  /** Placeholder text nudging the player toward a good first spell. */
  defaults?: { chant: string; description: string };
  deadline?: number | null;
  submitting?: boolean;
  onSubmit: (result: CraftResult) => void;
}

interface Preview {
  summary: string;
  publicSummary: string;
  stats: SpellStats;
  notes: string[];
  interpreter: 'ai' | 'local';
  color: string;
  error?: string | null;
}

export function SpellCraft({
  type,
  title,
  intro,
  defaults,
  deadline,
  submitting,
  onSubmit,
}: SpellCraftProps) {
  const [chant, setChant] = useState(defaults?.chant ?? '');
  const [description, setDescription] = useState(defaults?.description ?? '');
  const [samples, setSamples] = useState<GesturePoint[][]>([]);
  const [strokeInfo, setStrokeInfo] = useState({ points: 0, strokes: 0 });
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [remaining, setRemaining] = useState<number | null>(null);
  const recorderRef = useRef<GestureRecorder | null>(null);
  const [padKey, setPadKey] = useState(0);

  const ghost = useMemo(
    () => (samples.length > 0 ? samples[0].map((p) => ({ x: p.x, y: p.y, s: p.s })) : null),
    [samples],
  );

  // A different sample incantation each time the forge opens, so the word bank
  // below it reads as something to assemble from rather than a glossary.
  const placeholder = useMemo(
    () => LEXICON_EXAMPLES[Math.floor(Math.random() * LEXICON_EXAMPLES.length)],
    [],
  );

  /* --- craft timer --------------------------------------------------- */
  useEffect(() => {
    if (!deadline) {
      setRemaining(null);
      return;
    }
    const tick = () => setRemaining(Math.max(0, Math.ceil((deadline - Date.now()) / 1000)));
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [deadline]);

  /* --- speech capture for the chant field ---------------------------- */
  useEffect(() => {
    if (!listening) return;
    const off = chantRecognizer.onUpdate(({ transcript, isFinal }) => {
      setChant(transcript.trim().slice(0, 48));
      if (isFinal) setListening(false);
    });
    const started = chantRecognizer.start();
    if (!started) setListening(false);
    return () => {
      off();
    };
  }, [listening]);

  const saveSample = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.points.length < 6) return;
    recorder.endStroke();
    const points = recorder.finish();
    setSamples((prev) => [...prev, points].slice(0, 2));
    setStrokeInfo({ points: 0, strokes: 0 });
    setPadKey((k) => k + 1);
    setPreview(null);
    sfx.ui();
  }, []);

  const clearSamples = useCallback(() => {
    setSamples([]);
    setStrokeInfo({ points: 0, strokes: 0 });
    setPadKey((k) => k + 1);
  }, []);

  /**
   * Ask the server for an interpretation. Falls back to the in-browser
   * interpreter when the server is unreachable — the tutorial has to work
   * before the player has joined anything, and a dead network must not block a
   * craft phase either.
   */
  const interpret = useCallback(async () => {
    if (!chant.trim() || !description.trim()) return;
    setBusy(true);
    try {
      const res = await fetch(`${serverHttpUrl()}/api/interpret`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ type, chant, description }),
      });
      if (!res.ok) throw new Error('interpret failed');
      const data = (await res.json()) as Preview & { ok: boolean };
      setPreview({
        summary: data.summary,
        publicSummary: data.publicSummary,
        stats: data.stats,
        notes: data.notes ?? [],
        interpreter: data.interpreter,
        color: data.color,
        error: data.error ?? null,
      });
    } catch {
      const local = interpretLocally({ type, chant, description });
      const balanced = balanceFromInterpretation(type, normalizeChant(chant) || 'SPELL', local);
      setPreview({
        summary: local.summary,
        publicSummary: local.publicSummary,
        stats: balanced.stats,
        notes: [...balanced.notes, '서버에 연결할 수 없어 로컬 해석기를 사용했습니다.'],
        interpreter: 'local',
        color: local.color,
      });
    } finally {
      setBusy(false);
      sfx.circle();
    }
  }, [chant, description, type]);

  const consistency = useMemo(() => {
    if (samples.length < 2) return null;
    return buildTemplate(samples).selfConsistency;
  }, [samples]);

  const canInterpret = chant.trim().length > 0 && description.trim().length > 0;
  const canSubmit = canInterpret && samples.length >= 1 && preview !== null && !submitting;

  /**
   * What the chant alone has already decided. Shown live, because the length of
   * the incantation is the one cost the player is still free to change while
   * they are assembling it — once they hit "해석하기" the rest is the rules'.
   */
  const estimate = useMemo(
    () => (chant.trim() ? estimateChant(type, normalizeChant(chant)) : null),
    [chant, type],
  );
  const chantRoom = CHANT_MAX - chant.trim().length;

  /** Append a token from the word bank, spacing it and respecting the cap. */
  const appendToken = useCallback((word: string) => {
    setChant((prev) => {
      const base = prev.trim();
      const next = base ? `${base} ${word}` : word;
      return next.length > CHANT_MAX ? prev : next;
    });
    setPreview(null);
    sfx.ui();
  }, []);

  const dropLastToken = useCallback(() => {
    setChant((prev) => prev.trim().split(/\s+/).slice(0, -1).join(' '));
    setPreview(null);
    sfx.ui();
  }, []);

  return (
    <div className="panel craft" data-testid="spell-craft">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2 className="title">{title}</h2>
          {intro}
        </div>
        {remaining !== null ? (
          <div style={{ textAlign: 'right' }}>
            <div className="hint">남은 시간</div>
            <div className="countdown-chip" data-testid="craft-timer">
              {remaining}s
            </div>
          </div>
        ) : null}
      </div>

      <div className="craft__grid">
        <div>
          <div className="section-label">1. 영창 (English)</div>
          <div className="row">
            <input
              className="field"
              data-testid="craft-chant"
              value={chant}
              maxLength={CHANT_MAX}
              placeholder={placeholder}
              onChange={(e) => {
                setChant(e.target.value);
                setPreview(null);
              }}
            />
            <button
              type="button"
              className={`btn ${listening ? 'btn--danger' : ''}`}
              disabled={!speechSupported()}
              onClick={async () => {
                if (listening) {
                  chantRecognizer.stop();
                  setListening(false);
                  return;
                }
                await mic.start();
                setListening(true);
              }}
              title={speechSupported() ? '말해서 입력' : '이 브라우저는 음성 인식을 지원하지 않습니다'}
            >
              {listening ? '● 듣는 중' : '🎙 말하기'}
            </button>
          </div>
          {estimate ? (
            <dl className="chant-estimate" data-testid="chant-estimate">
              <div>
                <dt>음절</dt>
                <dd>{estimate.syllables}</dd>
              </div>
              <div>
                <dt>효과 예산</dt>
                <dd>
                  {estimate.budgetTotal}
                  {estimate.budgetBonus > 0 ? <em> +{estimate.budgetBonus}</em> : null}
                </dd>
              </div>
              <div>
                <dt>비용 배율</dt>
                <dd>×{estimate.costMultiplier.toFixed(2)}</dd>
              </div>
              <div>
                <dt>시전 시간</dt>
                <dd>{(estimate.castTimeMs / 1000).toFixed(2)}s</dd>
              </div>
            </dl>
          ) : null}
          <div className="hint" style={{ marginTop: 6 }}>
            {!estimate
              ? '전투 중 외칠 영어 문구를 정하세요. 아래 단어 사전을 눌러 이어 붙일 수 있습니다.'
              : estimate.bonusCapped
                ? '길이 보너스가 상한에 닿았습니다 — 더 길게 외워도 예산은 그대로이고 노출 시간만 늘어납니다.'
                : estimate.syllables <= 2
                  ? '짧고 빠르지만, 그만큼 마나와 재사용 시간이 비싸집니다.'
                  : '길게 외우는 만큼 효과 예산이 늘어나지만, 외우는 동안 무방비로 노출됩니다.'}
          </div>

          <div className="row lexicon__head">
            <span className="hint">
              단어 사전 · {chant.trim().length}/{CHANT_MAX}자
            </span>
            <button
              type="button"
              className="btn btn--ghost btn--small"
              data-testid="drop-token"
              disabled={!chant.trim()}
              onClick={dropLastToken}
            >
              ⌫ 마지막 단어
            </button>
          </div>
          <LexiconPicker remaining={chantRoom} onAppend={appendToken} />

          <div className="section-label">2. 원하는 기능</div>
          <textarea
            className="field"
            data-testid="craft-description"
            value={description}
            maxLength={400}
            rows={3}
            placeholder={
              type === 'parry'
                ? '예: 공격을 산산조각 내고 일부를 상대에게 돌려보낸다.'
                : '예: 적을 추적한 뒤 뒤에서 폭발하는 검은 구체'
            }
            onChange={(e) => {
              setDescription(e.target.value);
              setPreview(null);
            }}
          />
          <div className="hint" style={{ marginTop: 6 }}>
            자유롭게 쓰세요. 창의성이나 강함을 평가하지 않습니다 — 설명은 게임이 실행할 수 있는 효과 조합으로 번역될 뿐입니다.
          </div>

          <div className="section-label">3. 동작 등록 ({samples.length}/2)</div>
          <GesturePad
            key={padKey}
            label={samples.length === 0 ? '첫 번째 등록' : samples.length === 1 ? '두 번째 등록 (같은 모양으로)' : '등록 완료'}
            color={preview?.color ?? '#8fd8ff'}
            ghost={ghost}
            disabled={samples.length >= 2}
            onChange={(points, strokes) => setStrokeInfo({ points, strokes })}
            recorderRef={(r) => {
              recorderRef.current = r;
            }}
          />
          <div className="sample-chips">
            {[0, 1].map((i) => (
              <div key={i} className={`sample-chip ${samples[i] ? 'sample-chip--done' : ''}`}>
                {samples[i] ? `✓ ${i + 1}번째 등록 완료` : `${i + 1}번째 등록 대기`}
              </div>
            ))}
          </div>
          <div className="row" style={{ marginTop: 10 }}>
            <button
              type="button"
              className="btn"
              data-testid="save-gesture"
              disabled={strokeInfo.points < 6 || samples.length >= 2}
              onClick={saveSample}
            >
              이 동작 저장
            </button>
            <button type="button" className="btn btn--ghost" onClick={clearSamples} disabled={samples.length === 0}>
              전부 다시
            </button>
            {consistency !== null ? (
              <span className="hint">
                두 등록의 일치도 {Math.round(consistency * 100)}%
                {consistency < 0.55 ? ' — 많이 다릅니다. 판정이 관대해지지만 다시 그리는 편이 좋습니다.' : ''}
              </span>
            ) : null}
          </div>
        </div>

        <div>
          <div className="section-label">해석 결과</div>
          {preview ? (
            <>
              <div className="interpretation" data-testid="interpretation">
                {preview.summary}
                <div className="interpretation__meta">
                  {preview.interpreter === 'ai' ? 'AI 해석' : '로컬 해석기'} · 상대에게는 “{preview.publicSummary}”로만 보입니다
                </div>
              </div>

              <div className="section-label">고정 밸런스 규칙이 정한 수치</div>
              <dl className="stat-grid">
                {preview.stats.damage > 0 ? (
                  <>
                    <dt>{preview.stats.projectileCount > 1 ? '발당 피해' : '피해량'}</dt>
                    <dd>{preview.stats.damage}</dd>
                  </>
                ) : null}
                {preview.stats.projectileCount > 1 ? (
                  <>
                    <dt>발수</dt>
                    <dd data-testid="projectile-count">
                      {preview.stats.projectileCount}발 ·{' '}
                      {(preview.stats.burstIntervalMs / 1000).toFixed(2)}s 간격
                    </dd>
                    <dt>합계 피해</dt>
                    <dd>{Math.round(preview.stats.damage * preview.stats.projectileCount * 10) / 10}</dd>
                  </>
                ) : null}
                {preview.stats.healAmount > 0 ? (
                  <>
                    <dt>회복</dt>
                    <dd>{preview.stats.healAmount}</dd>
                  </>
                ) : null}
                {preview.stats.reflectRatio > 0 ? (
                  <>
                    <dt>반사 비율</dt>
                    <dd>{Math.round(preview.stats.reflectRatio * 100)}%</dd>
                  </>
                ) : null}
                {preview.stats.absorbRatio > 0 ? (
                  <>
                    <dt>마나 흡수</dt>
                    <dd>{Math.round(preview.stats.absorbRatio * 100)}%</dd>
                  </>
                ) : null}
                <dt>마나 소모</dt>
                <dd>{preview.stats.manaCost}</dd>
                <dt>재사용 시간</dt>
                <dd>{(preview.stats.cooldownMs / 1000).toFixed(1)}s</dd>
                <dt>시전 시간</dt>
                <dd>{(preview.stats.castTimeMs / 1000).toFixed(2)}s</dd>
                <dt>패링 가능</dt>
                <dd>{preview.stats.parryable ? '예' : '아니오'}</dd>
                <dt>효과 예산</dt>
                <dd>
                  {preview.stats.budgetUsed}/{preview.stats.budgetTotal}
                </dd>
              </dl>
              {preview.notes.length > 0 ? (
                <div className="hint" style={{ marginTop: 10 }}>
                  {preview.notes.map((n, i) => (
                    <div key={i}>· {n}</div>
                  ))}
                </div>
              ) : null}
            </>
          ) : (
            <div className="hint">
              영창과 기능을 적고 <b>해석하기</b>를 누르면, 한 문장으로 확인할 수 있습니다.
            </div>
          )}

          <div className="stack" style={{ marginTop: 18 }}>
            <button
              type="button"
              className="btn"
              data-testid="interpret"
              disabled={!canInterpret || busy}
              onClick={() => void interpret()}
            >
              {busy ? '해석 중…' : '해석하기'}
            </button>
            <button
              type="button"
              className="btn btn--primary"
              data-testid="confirm-spell"
              disabled={!canSubmit}
              onClick={() => {
                sfx.signature(1);
                onSubmit({ chant, description, gestureSamples: samples });
              }}
            >
              {submitting ? '등록 중…' : '이 주문으로 확정'}
            </button>
            {samples.length === 0 ? (
              <div className="hint">동작을 최소 한 번 등록해야 확정할 수 있습니다.</div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
